import { ref, computed } from 'vue'

import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'
import { getI18n } from '@/plugins/i18n'
import { useProductService } from '@/services/product.service'

import { isAdding } from '@/composables/data-grid'

export const useDocumentRow = (gridRef) => {
  const webSiteStore = useWebSiteStore()
  const garageStore = useGarageStore()
  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()

  const lastDocumentRowType = ref(null)
  const documentRowType = ref(webSiteStore.getDefaultDocumentRowType())
  const documentRowProductPurchaseFamily = ref(null)

  const { setTotalVatValue } = useProductService()

  const batchDeleteSelectedRows = ref([])
  const selectedRows = ref([])

  const isRoundingExpenseRow = computed(() => documentRowType.value === webSiteStore.documentRowTypesEnum.EXPENSE && Object.values(portalStore.hiddenPurchaseFamilyCodes).includes(documentRowProductPurchaseFamily.value?.code))
  const isEditableRoundingPercentage = computed(() => documentRowProductPurchaseFamily.value?.code === portalStore.hiddenPurchaseFamilyCodes.EXPENSE_PERCENTAGE)

  const openRoundingRow = ref(false)
  const roundingProduct = ref({})

  const setDocumentRowProductPurchaseFamily = (rowData = null) => {
    const purchaseFamily = portalStore.getPurchaseFamilyById(rowData?.product_purchase_family_id || rowData?.purchase_family_id) || null
    documentRowProductPurchaseFamily.value = purchaseFamily
  }

  const initializeRoundingRowData = (props, rowData, isEdit = false) => {
    rowData.vat_type_ref = rowData?.vat_type_ref ?? props.data?.customer?.vat_type_ref ?? registryStore.companyData.default_vat_type_ref
    const totalVat = props.data?.total_vat ?? 0

    if (isEdit) {
      rowData.document_total_vat = totalVat - (rowData.total_vat || 0)
      rowData.rounding = rowData.total_vat || 0
    } else {
      rowData.document_total_vat = totalVat
      rowData.rounding = (openRoundingRow.value ? Math.floor(totalVat) - totalVat : rowData?.rounding) ?? 0
    }

    rowData.rounding_percentage = isEditableRoundingPercentage.value ? (totalVat !== 0 ? rowData.rounding / rowData.document_total_vat : 0) : (rowData?.rounding_percentage ?? 0)
    const total = rowData.document_total_vat + rowData.rounding
    rowData.rounded_total = openRoundingRow.value ? Math.floor(total) : total
  }

  const applyRoundingPercentageFromProduct = (product = null, setCellValueByField) => {
    const purchaseFamilyCode = portalStore.getPurchaseFamilyById(product?.purchase_family_id)?.code ?? documentRowProductPurchaseFamily.value?.code

    if (!Object.values(portalStore.hiddenPurchaseFamilyCodes).includes(purchaseFamilyCode)) return

    if (purchaseFamilyCode === portalStore.hiddenPurchaseFamilyCodes.EXPENSE_PERCENTAGE) {
      setCellValueByField('rounding_percentage', product?.value_percent ?? 0)
      return
    }

    if (purchaseFamilyCode === portalStore.hiddenPurchaseFamilyCodes.ROUNDING) {
      const editingRow = gridRef.value?.instance?.getVisibleRows().find(row => row.isEditing)
      const documentTotalVat = editingRow?.data?.document_total_vat ?? 0
      setCellValueByField('rounded_total', Math.floor(documentTotalVat))
      return
    }

    setCellValueByField('rounding_percentage', 0)
  }

  const onDocumentRowUpdated = (e) => {
    lastDocumentRowType.value = null
    if (e.data.document_row_type) {
      lastDocumentRowType.value = e.data.document_row_type
    }
  }

  const onDocumentRowEditorPreparing = (e, isToolbarDisabled, supplierBrand, includesVat) => {
    isToolbarDisabled.value = !isAdding.value && !!e.row?.data?.id

    if (e.parentType === 'dataRow') {
      const includesVatValue = typeof includesVat === 'object' ? includesVat?.value : includesVat
      const noVatFields = ['price', 'total_no_vat']
      const vatFields = ['price_vat', 'total_vat']
      const readOnlyFields = includesVatValue ? noVatFields : vatFields

      if (readOnlyFields.includes(e.dataField)) {
        e.editorOptions.readOnly = true
      }
    }

    if (e.parentType === 'dataRow' && e.dataField === 'supplier_brand_ref') {
      if (supplierBrand.value) {
        const defaultValueChangeHandler = e.editorOptions.onValueChanged
        e.editorOptions.onValueChanged = (args) => {
          supplierBrand.value = registryStore.getSupplierBrandById(args.value)
          defaultValueChangeHandler(args)
        }
      }
    }

    if (e.parentType === 'dataRow' && e.dataField === 'document_row_type') {
      documentRowType.value = e.row.data.document_row_type
    }

    if (e.parentType === 'dataRow' && e.dataField === 'total_vat') {
      const defaultValueChanged = e.editorOptions.onValueChanged
      e.editorOptions.onValueChanged = (arg) => {
        defaultValueChanged?.({ ...arg, value: arg.value + 1 })
        setTimeout(() => defaultValueChanged?.({...arg, value: arg.value}), 0) // Hack to force re-calculation
      }
    }
  }

  const onDocumentRowEditorPreparingSetIsReadOnly = (e, isReadOnly = null, editors = null) => {
    if (isReadOnly && e.parentType === 'dataRow') {
      e.editorOptions.readOnly = true
    }

    if (e.row?.data?.document_row_type === webSiteStore.documentRowTypesEnum.EXPENSE) {
      const discountFields = ['discount', 'discount_2', 'discount_3', 'charge_percentage']
      const priceFields = ['total_no_vat', 'total_vat', 'price', 'price_vat']

      if (e.parentType === 'dataRow' && priceFields.includes(e.dataField)) {
        const defaultValueChanged = e.editorOptions.onValueChanged

        e.editorOptions.onValueChanged = (arg) => {
          defaultValueChanged(arg)
          discountFields.forEach(field => { editors.value[field].option('readOnly', isReadOnly) })
          if (arg.value < 0) {
            discountFields.forEach(field => { editors.value[field].option('readOnly', true) })
          }
        }
      }

      if (e.parentType === 'dataRow' && discountFields.includes(e.dataField)) {
        e.editorOptions.readOnly = e.row?.data?.price < 0 || e.row?.data?.total_no_vat < 0 || isReadOnly
      }
    }
  }

  const focusQuantityEditor = (editors) => {
    setTimeout(() => editors.value['quantity']?.focus?.(), 100)
  }

  const onDocumentRowInitNewRoundingRow = (e) => {
    if (openRoundingRow.value) {
      e.data.document_row_type = documentRowType.value = webSiteStore.documentRowTypesEnum.EXPENSE
      documentRowProductPurchaseFamily.value = portalStore.purchaseFamilies.find(f => f.code === portalStore.hiddenPurchaseFamilyCodes.ROUNDING) ?? null
      e.data.product_ref = roundingProduct.value.id
      e.data.description = roundingProduct.value.name
      e.data.discount = 0
      e.data.quantity = 1
      setTotalVatValue(e.data, e.data.rounding, e.data)
    }
  }

  const onDocumentRowInitNewRow = (e, supplierBrand, discount, isToolbarDisabled, defaultDeposit = null, defaultGarage = null, documentInterventionTypeRef = null) => {
    isToolbarDisabled.value = false
    setDocumentRowProductPurchaseFamily()
    e.data.quantity = 1
    e.data.price = 0
    e.data.price_vat = 0
    e.data.deposit = defaultDeposit
    e.data.garage = defaultGarage
    e.data.document_row_type = documentRowType.value = e.data?.document_row_type || webSiteStore.getDefaultDocumentRowType()
    e.data.cost_valorization = garageStore.costValorizationsEnum.NORMAL

    if (e.data.document_row_type === webSiteStore.documentRowTypesEnum.PRODUCT) {
      e.data.supplier_brand_ref = supplierBrand.value?.id
      e.data.charge_percentage = supplierBrand.value?.charge_percentage || 0
      const rowInterventionType = garageStore.interventionTypes.find(item => item.id === e.data.intervention_type_ref)
      const documentInterventionType = garageStore.interventionTypes.find(item => item.id === documentInterventionTypeRef)
      const isWarrantyRow = rowInterventionType?.code === webSiteStore.interventionTypeCodeEnum.WARRANTY
      const isWarrantyDocumentDiscount = discount === 1 && documentInterventionType?.code === webSiteStore.interventionTypeCodeEnum.WARRANTY

      e.data.discount = isWarrantyRow ? 1 : (isWarrantyDocumentDiscount ? 0 : discount)
    }

    if (e.data.document_row_type === webSiteStore.documentRowTypesEnum.TEXT) {
      e.data.total_no_vat = 0
      e.data.total_vat = 0
    }

    if (openRoundingRow.value) {
      e.data.supplier_brand_ref = supplierBrand.value?.id
      onDocumentRowInitNewRoundingRow(e)
      openRoundingRow.value = false
    }
  }

  const onDocumentRowTypeChanged = (rowType, getEditColumns = () => []) => {
    if (!rowType) { rowType === documentRowType.value }
    gridRef.value.instance.option('editing.form.items', getEditColumns(rowType))
  }

  const onDocumentRowSelectionChanged = (e) => {
    batchDeleteSelectedRows.value = e.selectedRowsData
  }

  const getRoundingExpenseColumns = (documentRowType, currency, dataGrid) => {
    const i18n = getI18n()
    return [{
      title: i18n.t('common.rounding'),
      items: [
        { itemType: 'group', caption: i18n.t('common.rounding'), colCount: 3, items: [
        { dataField: 'document_total_vat', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, readOnly: true}) } },
        { itemType: 'empty', colSpan: 2 },
        { dataField: 'vat_type_ref', editorOptions: { showClearButton: true } },
        { dataField: 'rounding', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
        { dataField: 'rounding_percentage', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), min: 0, readOnly: !isEditableRoundingPercentage.value } },
        { dataField: 'rounded_total', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency}) } },
        ]}
      ]
    }]
  }

  const getEditMainInfoColumns = (documentRowType, dataGrid, processingItem = null, warehouseDocument = false) => {
    const i18n = getI18n()
    const isLabor = documentRowType === webSiteStore.documentRowTypesEnum.LABOR
    const processingFormItem = processingItem ?? { itemType: 'empty', colSpan: 8 }
    const rowTypeColSpan = processingItem ? 6 : 4

    const getWarehouseQuantityFields = () => [
      { dataField: 'quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: 3 },
      { dataField: 'expected_quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: 3 },
      { dataField: 'unit_of_measure_ref', colSpan: 3 },
    ]

    let mainInfoDataFields = []
    if (documentRowType === webSiteStore.documentRowTypesEnum.EXPENSE && (isRoundingExpenseRow.value)) {
      mainInfoDataFields = [
        { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: rowTypeColSpan },
        processingFormItem,
        { dataField: 'supplier_brand_ref', editorOptions: { readOnly: true }, colSpan: 4 },
        { dataField: 'product_ref', colSpan: 8 },
        { dataField: 'description', colSpan: 12 },
      ]
    } else if (documentRowType === webSiteStore.documentRowTypesEnum.TEXT) {
      mainInfoDataFields = [
        { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: rowTypeColSpan },
        // { dataField: 'row_number', editorOptions: { readOnly: true }, colSpan: 8 }, --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
        processingFormItem,
        { dataField: 'description', colSpan: 12 },
        { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
      ]
    } else if (documentRowType === webSiteStore.documentRowTypesEnum.FREE) {
      mainInfoDataFields = [
        { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: rowTypeColSpan },
        // { dataField: 'row_number', editorOptions: { readOnly: true }, colSpan: 8 }, --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
        processingFormItem,
        { dataField: 'description', colSpan: 12 },
        { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
        ...(warehouseDocument
          ? getWarehouseQuantityFields()
          : [
            { dataField: 'quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: 6 },
            { dataField: 'unit_of_measure_ref', colSpan: 6 },
          ]),
      ]
    } else {
      mainInfoDataFields = [
        { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: rowTypeColSpan },
        // { dataField: 'row_number', editorOptions: { readOnly: true }, colSpan: 8 }, --- US 623 https://dev.azure.com/softway-it/ArcoWeb/_workitems/edit/623
        processingFormItem,
        { dataField: 'supplier_brand_ref', editorOptions: { showClearButton: true }, colSpan: 4 },
        { dataField: 'product_ref', colSpan: 8 },
        { dataField: 'description', colSpan: 12 },
        { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
        ...(warehouseDocument
          ? getWarehouseQuantityFields()
          : [
            { dataField: 'quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: isLabor ? 3 : 6 },
            { dataField: 'unit_of_measure_ref', colSpan: isLabor ? 3 : 6 },
          ]),
      ]
      if (documentRowType === webSiteStore.documentRowTypesEnum.LABOR) {
        mainInfoDataFields.push(
          { dataField: 'timeEquivalent', editorOptions: { readOnly: true }, visible: true, colSpan: 3 },
        )
      }
    }
    return mainInfoDataFields
  }

  const getEditAdditionalInfoColumns = (documentRowType, currency, dataGrid) => {
    const { t } = getI18n()
    return [
      { dataField: 'cost', editorOptions: { readOnly: true, ...dataGrid.useCurrencyOptions({ currency }) } },
      { dataField: 'cost_valorization' },
      { dataField: 'discount_category_ref', colSpan: 2, editorOptions: { readOnly: true } },
      { dataField: 'product_price', editorOptions: dataGrid.useCurrencyOptions({ currency, readOnly: true }) },
      { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions() },
      { dataField: 'price', label: { text: t('common.unitPriceNoVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({ currency, precision: registryStore.companyData.default_display_price_precision }), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
      { dataField: 'price_vat', label: { text: t('common.unitPriceVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({ currency, precision: registryStore.companyData.default_display_price_precision }), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
      ...['discount', 'discount_2', 'discount_3'].map(dataField => ({ dataField, editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } })),
      { dataField: 'total_discount', editorOptions: { ...dataGrid.usePercentageOptions(), readOnly: true } },
      { dataField: 'vat_type_ref', editorOptions: { showClearButton: true }, colSpan: 2 },
      { dataField: 'total_no_vat', editorOptions: { ...dataGrid.useCurrencyOptions({ currency, readOnly: true }), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
      { dataField: 'total_vat', editorOptions: { ...dataGrid.useCurrencyOptions({ currency, readOnly: true }), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
    ]
  }

  return {
    batchDeleteSelectedRows,
    documentRowType,
    documentRowProductPurchaseFamily,
    isRoundingExpenseRow,
    isEditableRoundingPercentage,
    lastDocumentRowType,
    selectedRows,
    openRoundingRow,
    roundingProduct,
    getEditMainInfoColumns,
    getEditAdditionalInfoColumns,
    getRoundingExpenseColumns,
    setDocumentRowProductPurchaseFamily,
    onDocumentRowUpdated,
    onDocumentRowInitNewRow,
    onDocumentRowEditorPreparing,
    onDocumentRowEditorPreparingSetIsReadOnly,
    onDocumentRowSelectionChanged,
    onDocumentRowTypeChanged,
    applyRoundingPercentageFromProduct,
    initializeRoundingRowData,
    focusQuantityEditor,
  }
}
